'use client';
import { useEffect, useState } from 'react';
import { getToken, isSupported } from 'firebase/messaging';

import { messaging } from '../firebase/firebaseConfig';

import { useNotificationPermissionStatus } from './useNotificationPermissionStatus';

export type FCMPhase = 'enabling' | 'enabled' | 'issue';
export interface FCMTokenStatus {
  phase: FCMPhase;
  reason?: string;
}

const isMessagingSW = (url?: string | null) =>
  !!url && /\/firebase-messaging-sw\.js(\?.*)?$/.test(url);

export const useFCMTokenWithStatus = () => {
  const permission = useNotificationPermissionStatus();
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [status, setStatus] = useState<FCMTokenStatus>({ phase: 'enabling' });

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
        setStatus({ phase: 'issue', reason: 'no-sw' });
        setFcmToken(null);
        return;
      }

      if (permission === 'denied') {
        setStatus({ phase: 'issue', reason: 'denied' });
        setFcmToken(null);
        return;
      }
      if (permission === 'default') {
        setStatus({ phase: 'enabling' });
        setFcmToken(null);
        return;
      }

      if (!(await isSupported())) {
        setStatus({ phase: 'issue', reason: 'fcm-not-supported' });
        setFcmToken(null);
        return;
      }

      setStatus({ phase: 'enabling' });

      const reg = await navigator.serviceWorker.ready;
      const controllerScript =
        navigator.serviceWorker.controller?.scriptURL ?? null;
      const swScript = reg.active?.scriptURL ?? null;
      const ok = isMessagingSW(controllerScript) && isMessagingSW(swScript);

      if (!ok) {
        setStatus({ phase: 'enabling', reason: 'controller-not-messaging-sw' });
        setFcmToken(null);
        return;
      }

      try {
        setStatus({ phase: 'enabling' });
        const token = await getToken(messaging(), {
          vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
          serviceWorkerRegistration: reg,
        });
        if (cancelled) return;
        if (token) {
          setFcmToken(token);
          setStatus({ phase: 'enabled' });
        } else {
          setFcmToken(null);
          setStatus({ phase: 'issue', reason: 'empty-token' });
        }
      } catch (err) {
        if (cancelled) return;
        setFcmToken(null);
        setStatus({
          phase: 'issue',
          reason: (err as Error)?.message || 'getToken-failed',
        });
      }
    };

    if (!('serviceWorker' in navigator)) {
      return;
    }

    void run();
    const onControllerChange = () => void run();

    navigator.serviceWorker.addEventListener(
      'controllerchange',
      onControllerChange,
    );

    return () => {
      cancelled = true;
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange,
      );
    };
  }, [permission]);

  return { fcmToken, status } as const;
};

// Backwards-compatible hook: returns only the token
export const useFCMToken = () => {
  const { fcmToken } = useFCMTokenWithStatus();
  return fcmToken;
};
